import { DATE_FORMAT } from '@/constants/common.constant'
import { AxiomReportStatus, AxiomReportType } from '@/enums/axiom-report.enum'
import { setSelectedReport } from '@/states/axiomReport.slice'
import {
  legacyDefaultGridOptions,
  sideBarDefaultConfig,
  themeClassName,
} from '@/utils/grid-utils/ag-grid-config'
import { defaultColDefs } from '@/utils/grid-utils/colDefs'
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { useAppDispatch } from '@hooks/use-app-dispatch'
import { IReportDTO } from '@interfaces/report.interface'
import { Button } from '@mui/material'
import {
  customDateComparator,
  getUpdatedColumnsWithTooltip,
  gridDateFormatter,
  gridDateTimeFormatter,
} from '@utils/tableGridUtils'
import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'

export type IStaticReportsList = {
  rowData: any
}

const StaticReportsList: React.FC<IStaticReportsList> = ({ rowData }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const columnDefs: ColDef[] = [
    {
      headerName: 'Report ID',
      field: 'reportId',
      cellRenderer: (params: any) => {
        return (
          <Button
            type='button'
            variant='text'
            color={
              params.data?.messageText === AxiomReportStatus.COMPLETED
                ? 'primary'
                : 'secondary'
            }
            sx={{
              minWidth: 0,
              padding: 0,
              fontSize: 'inherit',
              pointerEvents:
                params.data?.messageText === AxiomReportStatus.COMPLETED
                  ? 'initial'
                  : 'none',
            }}
            disableRipple
            disableFocusRipple
            disableTouchRipple
            disableElevation
            onClick={() => {
              loadReport(params.data)
            }}
          >
            {params.data.reportId}
          </Button>
        )
      },
    },
    {
      headerName: 'Portfolio',
      field: 'portfolioName',
    },
    {
      headerName: 'Report Type',
      field: 'reportTypeLongName',
    },
    {
      headerName: 'Created Time',
      field: 'reportBeginTime',
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: customDateComparator,
        buttons: ['reset'],
      },
      minWidth: 140,
      tooltipField: undefined,
      valueFormatter: (params: ValueFormatterParams) =>
        gridDateTimeFormatter(params, `${DATE_FORMAT.TABLE_CHART} hh:mm A`),
      tooltipValueGetter: (params: ITooltipParams) =>
        gridDateTimeFormatter(params, `${DATE_FORMAT.TABLE_CHART} hh:mm A`),
    },
    {
      headerName: 'Completed Time',
      field: 'reportEndTime',
      sort: 'desc',
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: customDateComparator,
        buttons: ['reset'],
      },
      minWidth: 140,
      tooltipField: undefined,
      valueFormatter: (params: ValueFormatterParams) =>
        gridDateTimeFormatter(params, `${DATE_FORMAT.TABLE_CHART} hh:mm A`),
      tooltipValueGetter: (params: ITooltipParams) =>
        gridDateTimeFormatter(params, `${DATE_FORMAT.TABLE_CHART} hh:mm A`),
    },
    {
      headerName: 'Start Date',
      field: 'beginDate',
      filter: 'agDateColumnFilter',
      tooltipField: undefined,
      valueFormatter: (params: ValueFormatterParams) =>
        gridDateFormatter(params, DATE_FORMAT.DATE_FORMAT_1),
      filterParams: {
        comparator: customDateComparator,
        buttons: ['reset'],
      },
      tooltipValueGetter: (params: ITooltipParams) =>
        gridDateFormatter(params, DATE_FORMAT.DATE_FORMAT_1),
    },
    {
      headerName: 'End Date',
      field: 'endDate',
      filter: 'agDateColumnFilter',
      tooltipField: undefined,
      valueFormatter: (params: ValueFormatterParams) =>
        gridDateFormatter(params, DATE_FORMAT.DATE_FORMAT_1),
      filterParams: {
        comparator: customDateComparator,
        buttons: ['reset'],
      },
      tooltipValueGetter: (params: ITooltipParams) =>
        gridDateFormatter(params, DATE_FORMAT.DATE_FORMAT_1),
    },
    {
      headerName: 'Template',
      field: 'templateName',
    },
    {
      headerName: 'Benchmark',
      field: 'benchmarkName',
    },
    {
      headerName: 'Matrix',
      field: 'matrixName',
    },
    {
      headerName: 'Status',
      field: 'messageText',
    },
  ]

  const gridRef = useRef<AgGridReact>(null)

  const loadReport = (reportData: IReportDTO) => {
    dispatch(setSelectedReport(reportData))
    if (reportData.reportType === AxiomReportType.RV) {
      navigate(`/reports/${reportData.reportId}/rv/dashboard`)
    } else if (reportData.reportType === AxiomReportType.GFPA || reportData.reportType === AxiomReportType.GFPAH) {
      navigate(`/reports/${reportData.reportId}/gfpa/dashboard`)
    } else if (reportData.reportType === AxiomReportType.GFR) {
      navigate(`/reports/${reportData.reportId}/gfr/dashboard`)
    } else if (reportData.reportType === AxiomReportType.SCEN) {
      navigate(`/reports/${reportData.reportId}/scenario-analysis/dashboard`)
    } else if (reportData.reportType === AxiomReportType.OPTIM) {
      navigate(`/optimization-reports/${reportData.reportId}/optim/reports`)
    }
  }

  const gridOptions: GridOptions = {
    ...legacyDefaultGridOptions,
    defaultColDef: {
      minWidth: 100,
      filter: true,
    },
    autoGroupColumnDef: {
      minWidth: 200,
    },
  }

  const onGridReady = (params: GridReadyEvent) => {
    if (document.body.clientWidth > 1400) {
      params.api.sizeColumnsToFit()
    } else {
      params.api.autoSizeAllColumns()
    }
  }

  return (
    <div
      className={`${themeClassName}`}
      style={{ paddingBottom: '2rem', height: '100%' }}
      data-cy='static-reports-ag-grid'
    >
      <AgGridReact
        ref={gridRef}
        onGridReady={onGridReady}
        defaultColDef={{ ...defaultColDefs, cellStyle: { textAlign: 'left' } }}
        gridOptions={gridOptions}
        columnDefs={getUpdatedColumnsWithTooltip(columnDefs)}
        rowData={rowData}
        sideBar={sideBarDefaultConfig}
      ></AgGridReact>
    </div>
  )
}

export default StaticReportsList
